import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import styled, { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { device } from 'vqp-common';
import { HeaderBox } from './components/header';
import { NavBar } from './components/navigation';
import NotificationCenter from './components/common/NotificationCenter';
import { useDispatch } from 'react-redux';
import { fetchStoreInformation } from './redux/actions/store';
import ReactLoading from 'react-loading';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import OrdersPage from './pages/OrdersPage';
import ProductsPage from './pages/ProductsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import CouponsCreatePage from './pages/Cupons/Create/CouponsCreate';
import CouponsSearchPage from './pages/Cupons/Search/CouponsSearch';

import { THEME } from './config';
import isLoggedIn from './components/login/utilities/isLoggedIn';
import { GetQueryString } from './utils/QueryString';
import { setAccessToken, startApp } from './redux/actions/auth';
import { StoreProvider, useStore } from './providers/store.provider';
import OpeningHoursPage from './pages/OpeningHours/OpeningHoursPage';
import ReplicateMenuPage from './pages/ReplicateMenu/ReplicateMenuPage';
import LayoutSearchPage from './pages/Layout/Search/LayoutSearch';
import LayoutCreatePage from './pages/Layout/Create/LayoutCreate';

const Wrapper = styled.div`
  background: #fff;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const MainContainer = styled.div`
  display: flex
  flex-direction: row
`;

const Content = styled.div`
  position: absolute;
  top: ${(props) => props.theme.dimensions.headerHeightDesktop}px;
  left: ${(props) => props.theme.dimensions.navBarWidth}px;
  width: calc(100% - ${(props) => props.theme.dimensions.navBarWidth}px);
  overflow-y: auto;
  overflow-x: hidden;

  @media ${device.mobile} {
    width: 100%;
    left: 0;
  }
`;

const routes = [
  { path: '/replicate-menu', component: ReplicateMenuPage, accessControlName: 'replicaCardapio', exact: true },
  { path: '/products', component: ProductsPage, accessControlName: 'cardapio', exact: true },
  { path: '/orders', component: OrdersPage, accessControlName: 'pedidos', exact: true },
  { path: '/clients', component: ClientsPage, accessControlName: 'clientes', exact: true },
  { path: '/dashboard', component: DashboardPage, accessControlName: 'dashboard', exact: true },
  { path: '/settings', component: SettingsPage, accessControlName: 'minhaLoja', exact: true },
  { path: '/opening-hours', component: OpeningHoursPage, accessControlName: 'minhaLoja', exact: true },
  { path: '/layout', component: LayoutSearchPage, accessControlName: 'minhaLoja', multistoreOnly: true, exact: true },
  { path: '/layout-cadastro', component: LayoutCreatePage, accessControlName: 'minhaLoja', multistoreOnly: true, exact: true },
  { path: '/cupom-consulta', component: CouponsSearchPage, accessControlName: 'cupons', exact: true },
  { path: '/cupom-cadastro', component: CouponsCreatePage, accessControlName: 'cupons', exact: true },
  { path: '/', component: DashboardPage, accessControlName: 'dashboard', exact: true }
];

const isMultistoreSession = () => {
  try {
    return JSON.parse(localStorage.getItem('multistore') || 'false') === true;
  } catch (e) {
    return false;
  }
};

const AuthenticatedRoutes = ({ accessControl }) => {
  const storeProvider = useStore();
  const isMultistore = Boolean(storeProvider?.isMultistore) || isMultistoreSession();

  return (
    <Switch>
      <Redirect exact from="/multilojas" to="/" />
      {routes
        .filter((route) => accessControl[route.accessControlName])
        .filter((route) => !route.multistoreOnly || isMultistore)
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            exact={route.exact}
            component={route.component}
          />
        ))}
    </Switch>
  );
};

const MainApp = ({ storeInfo }) => {
  const dispatch = useDispatch();
  const { accessControl } = useSelector((state) => state.settings);

  const [error, setError] = useState('');
  const localAccess = localStorage.getItem('auth') ? localStorage.getItem('auth') : null;
  const logout = localStorage.getItem('logout') ? localStorage.getItem('logout') : null;
  const [loading, setLoading] = useState(true);

  const title = process.env.REACT_APP_TITLE || '';

  useEffect(() => {
    const qs = GetQueryString();

    const { token = '' } = qs;

    async function postAccessToken(accessToken) {
      setError();
      const response = await dispatch(setAccessToken(accessToken));

      if (response.success) {
        const infoResponse = await dispatch(fetchStoreInformation());
        if (infoResponse.success) {
          setLoading(false);
        } else {
          setLoading(false);
          setError(infoResponse.error);
        }
      }
    }

    if (logout) {
      dispatch(startApp(false));
    }

    if (!localAccess && !logout) {
      if (token) {
        postAccessToken(token);
        // ⚠️ Remove o token da URL para não ficar no histórico do navegador
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [logout, localAccess, dispatch]);

  const createLoginPageWithParams = (path, multistore) => {
    return (
      <Route
        path={path}
        render={(props) => {
          return (
            <LoginPage
              {...props}
              multistore={multistore}
            />
          );
        }}
      />
    );
  };

  const combineThemes = () => {
    const newTheme = JSON.parse(JSON.stringify(THEME));
    if (!storeInfo) return newTheme;

    if (storeInfo.primaryColor) {
      newTheme.colors.primary = storeInfo.primaryColor;
    }
    if (storeInfo.secondaryColor) {
      newTheme.colors.secondary = storeInfo.secondaryColor;
    }
    if (storeInfo.storeLogo) {
      newTheme.logoImage = storeInfo.storeLogo;
    }
    if (storeInfo.storeName) {
      newTheme.storeName = storeInfo.storeName;
    }
    return newTheme;
  };
  if (loading) {
    return (
      <Wrapper>
        <ReactLoading
          type="spin"
          color="#173A6D"
          height={'3%'}
          width={'3%'}
        />
      </Wrapper>
    );
  } else if (!isLoggedIn()) {
    return (
      <ThemeProvider theme={THEME}>
        <StoreProvider>
          <NotificationCenter />
          <div className="App">
            {error}
            <Router>
              <Switch>
                {createLoginPageWithParams('/multilojas', true)}
                {createLoginPageWithParams('/', false)}
              </Switch>
            </Router>
          </div>
        </StoreProvider>
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={combineThemes(storeInfo)}>
        <StoreProvider>
          <Helmet>
            <title>
              {title} - {storeInfo.storeName || ''}
            </title>
          </Helmet>
          <NotificationCenter />
          <Router>
            <div className="App">
              <HeaderBox />
              <MainContainer>
                <NavBar accessControl={accessControl} />
                <Content>
                  <AuthenticatedRoutes accessControl={accessControl} />
                </Content>
              </MainContainer>
            </div>
          </Router>
        </StoreProvider>
      </ThemeProvider>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    storeInfo: state && state.storeInfo
  };
};

export default connect(mapStateToProps, {})(MainApp);
